"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { cn } from "src/lib/utils";
import {
  SIDE_PANEL_FOCUSABLE_SELECTOR,
  useSidePanelLayout,
  type SidePanelKind,
  type SidePanelLayout,
} from "src/components/ui/side-panel/SidePanelContext";
import {
  useInnermostSidePanelOutlet,
  useSidePanelProvider,
  type SidePanelOutletHandle,
  type SidePanelRegistration,
} from "src/components/ui/side-panel/SidePanelProvider";

const SidePanelOutletContext = createContext<SidePanelOutletHandle | null>(null);

/**
 * The outlet a `SidePanel` renders into: the deepest registered outlet when it is nested inside the
 * nearest one (a panel from the project navbar sits in the app outlet's page, but belongs under the
 * navbar with the project's panels), else the nearest one, else the deepest registered, else none
 * (the panel renders `position: fixed`).
 */
export function useSidePanelOutlet(): SidePanelOutletHandle | null {
  const nearest = useContext(SidePanelOutletContext);
  const innermost = useInnermostSidePanelOutlet();
  if (nearest && innermost?.ancestorIds.includes(nearest.id)) return innermost;
  return nearest ?? innermost;
}

/**
 * The narrowest the page gets beside pushed panels. Narrower than this and the column overlays the
 * page instead, so a grid is never squeezed to a sliver (two panels at 1280px, one L panel at
 * 1100px). Wherever the page keeps this much, it is pushed, at any width from 768px up.
 */
export const SIDE_PANEL_MIN_PAGE_WIDTH = 560;

const VISIBLE_COLUMN_PANEL_SELECTOR =
  "[data-side-panel-layer]:not([hidden]) > [data-side-panel]:not([hidden]), [data-side-panel-layer]:not([hidden]) > [data-side-panel-rail]";

export interface SidePanelOutletProps {
  /** The page. It shrinks beside open panels while it keeps `SIDE_PANEL_MIN_PAGE_WIDTH`. */
  children: ReactNode;
  /** Classes for the outlet root, a flex row holding the page region and the panel column. */
  className?: string;
  /** Classes for the page region, the element that directly wraps `children`. */
  pageClassName?: string;
}

/** Inspect and docked: one of each at a time. Tasks stack. */
function isSingleton(kind: SidePanelKind): boolean {
  return kind !== "task";
}

/**
 * Where side panels render, and the page shrinking beside them.
 *
 * - From 768px: an in-flow column right of the page, so the page (and any AG Grid in it) narrows
 *   and the clicked cell stays in view, as long as the page keeps `SIDE_PANEL_MIN_PAGE_WIDTH`.
 *   When the open panels leave it less, the column overlays the page's right edge instead, under
 *   the navbar.
 * - <768px: every panel is full screen.
 *
 * At most one docked and one inspect panel per outlet, docked nearest the page; opening another of
 * the same kind closes the old one. An old one holding unsaved work (`isEscapeDisabled` or
 * `isDirty`) is not closed: it stays mounted, hidden under the new one, and comes back when the new
 * one closes. Full screen shows one of them at a time: the one opened last
 * hides the other (still mounted, so it keeps its state) until it closes. Task panels render
 * above both, over a dimmed page.
 *
 * With no panel open the outlet renders only the page region, so pages look exactly as before.
 */
export function SidePanelOutlet({
  children,
  className,
  pageClassName,
}: Readonly<SidePanelOutletProps>) {
  const parent = useContext(SidePanelOutletContext);
  const provider = useSidePanelProvider();
  const registerOutlet = provider?.registerOutlet;
  const updateOutlet = provider?.updateOutlet;
  const id = useId();
  const depth = parent ? parent.depth + 1 : 0;
  const parentAncestorIds = parent?.ancestorIds;
  const parentId = parent?.id;
  const ancestorIds = useMemo(
    () => (parentId ? [...(parentAncestorIds ?? []), parentId] : []),
    [parentAncestorIds, parentId],
  );
  const viewportLayout = useSidePanelLayout();
  // Push only while the page keeps room beside the open panels; measured, since the outlet is
  // narrower than the viewport (the app sidebar, an outer outlet's panels).
  const [isTooNarrowToPush, setIsTooNarrowToPush] = useState(false);
  const layout: SidePanelLayout =
    viewportLayout === "push" && isTooNarrowToPush ? "overlay" : viewportLayout;

  const [panels, setPanels] = useState<SidePanelRegistration[]>([]);
  // The source of truth for registrations, read and written synchronously so registering never
  // runs side effects inside a state updater; `panels` mirrors it for rendering.
  const panelsRef = useRef<SidePanelRegistration[]>([]);
  // Panels a newer one of their kind replaced, while their owners close them; mirrored for render.
  const [replacedIds, setReplacedIds] = useState<ReadonlySet<string>>(() => new Set());
  const [dockedSlot, setDockedSlot] = useState<HTMLDivElement | null>(null);
  const [inspectSlot, setInspectSlot] = useState<HTMLDivElement | null>(null);
  const [taskSlot, setTaskSlot] = useState<HTMLDivElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const columnRef = useRef<HTMLDivElement | null>(null);
  const pageRef = useRef<HTMLDivElement | null>(null);
  const lastPageFocusRef = useRef<HTMLElement | null>(null);
  const replacedIdsRef = useRef(new Set<string>());
  const closeRequestedIdsRef = useRef(new Set<string>());
  const layoutRef = useRef(layout);

  // Full screen, an open column panel covers the page, which is then inert. Set here rather than as
  // a prop: when the last column panel closes, its focus return runs before the outlet re-renders,
  // so the page must stop being inert at that moment for focus to land in it.
  const syncPageInert = useCallback(() => {
    const page = pageRef.current;
    if (!page) return;
    // A replaced panel shows nothing, even while its owner has not closed it yet.
    const isCovered =
      layoutRef.current === "full-screen" &&
      panelsRef.current.some(
        (entry) => isSingleton(entry.kind) && !replacedIdsRef.current.has(entry.id),
      );
    if (isCovered) page.setAttribute("inert", "");
    else page.removeAttribute("inert");
  }, []);
  useLayoutEffect(() => {
    layoutRef.current = layout;
    syncPageInert();
  }, [layout, syncPageInert]);

  const registerPanel = useCallback(
    (registration: SidePanelRegistration) => {
      const others = panelsRef.current.filter((entry) => entry.id !== registration.id);
      if (isSingleton(registration.kind)) {
        for (const entry of others) {
          if (entry.kind !== registration.kind || replacedIdsRef.current.has(entry.id)) continue;
          // Unsaved work is never closed from under the user: that panel waits under the new one.
          if (entry.isProtected()) continue;
          // Before the old panel unmounts, so it leaves focus where it is.
          replacedIdsRef.current.add(entry.id);
          entry.markReplaced();
        }
      }
      panelsRef.current = [...others, registration];
      setPanels(panelsRef.current);
      setReplacedIds(new Set(replacedIdsRef.current));
      syncPageInert();
      return () => {
        replacedIdsRef.current.delete(registration.id);
        closeRequestedIdsRef.current.delete(registration.id);
        panelsRef.current = panelsRef.current.filter((entry) => entry.id !== registration.id);
        setPanels(panelsRef.current);
        setReplacedIds(new Set(replacedIdsRef.current));
        syncPageInert();
      };
    },
    [syncPageInert],
  );

  // Ask replaced panels to close. Their owners' state lives outside the outlet.
  useEffect(() => {
    for (const entry of panels) {
      if (!replacedIdsRef.current.has(entry.id) || closeRequestedIdsRef.current.has(entry.id)) {
        continue;
      }
      closeRequestedIdsRef.current.add(entry.id);
      entry.onReplaced();
    }
  }, [panels]);

  const activeIds = useMemo(() => {
    const ids = new Set<string>();
    const lastOfKind = new Map<SidePanelKind, string>();
    for (const entry of panels) {
      // Replaced is final: a replaced panel whose owner has not closed it yet never comes back.
      if (replacedIds.has(entry.id)) continue;
      if (isSingleton(entry.kind)) lastOfKind.set(entry.kind, entry.id);
      else ids.add(entry.id);
    }
    lastOfKind.forEach((panelId) => ids.add(panelId));
    return ids;
  }, [panels, replacedIds]);

  // Older panels of a kind kept for their unsaved work: mounted, but hidden under the newest.
  const stackedIds = useMemo(() => {
    const ids = new Set<string>();
    for (const entry of panels) {
      if (!activeIds.has(entry.id) && !replacedIds.has(entry.id)) ids.add(entry.id);
    }
    return ids;
  }, [activeIds, panels, replacedIds]);

  const hasColumnPanel = panels.some((entry) => isSingleton(entry.kind) && activeIds.has(entry.id));
  const hasTask = panels.some((entry) => entry.kind === "task");
  // Full screen shows one column panel: the one opened last covers the other, which stays mounted
  // (keeping its state) but hidden and out of the tab order until the newer one closes.
  const openColumnKinds = panels
    .filter((entry) => isSingleton(entry.kind) && activeIds.has(entry.id))
    .map((entry) => entry.kind);
  const coveredKind =
    layout === "full-screen" && openColumnKinds.length > 1 ? openColumnKinds[0] : null;
  const isDockedCovered = coveredKind === "docked";
  const isInspectCovered = coveredKind === "inspect";
  // Full screen, a column panel covers the page, so the page leaves the tab order (and F6) too.
  const isPageCovered = layout === "full-screen" && hasColumnPanel;

  // Measure whether the open panels leave the page its minimum width.
  useLayoutEffect(() => {
    const root = rootRef.current;
    const column = columnRef.current;
    if (!root || !column) return undefined;
    function measure() {
      if (!root || !column) return;
      const outletWidth = root.getBoundingClientRect().width;
      let panelsWidth = 0;
      column.querySelectorAll<HTMLElement>(VISIBLE_COLUMN_PANEL_SELECTOR).forEach((panel) => {
        panelsWidth += Number(panel.dataset.panelWidth ?? 0);
      });
      // Zero before layout (and in tests without layout): nothing to decide on.
      if (outletWidth === 0) return;
      setIsTooNarrowToPush(outletWidth - panelsWidth < SIDE_PANEL_MIN_PAGE_WIDTH);
    }
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(root);
    observer.observe(column);
    // Collapsing to a rail or expanding changes what the column holds without resizing the outlet.
    // Watch only what decides the panels' widths (a panel or layer hiding, a width changing, a layer
    // joining a slot), not every change inside panel bodies, since each measure forces a layout.
    const mutations = new MutationObserver(measure);
    mutations.observe(column, {
      subtree: true,
      attributes: true,
      attributeFilter: ["hidden", "data-panel-width"],
    });
    column.querySelectorAll("[data-side-panel-slot]").forEach((slot) => {
      mutations.observe(slot, { childList: true });
    });
    return () => {
      observer.disconnect();
      mutations.disconnect();
    };
  }, [panels]);

  // Remember where focus was in the page, so F6 can go back there.
  useEffect(() => {
    const page = pageRef.current;
    if (!page) return undefined;
    function handleFocusIn(event: FocusEvent) {
      if (event.target instanceof HTMLElement && event.target !== page) {
        lastPageFocusRef.current = event.target;
      }
    }
    page.addEventListener("focusin", handleFocusIn);
    return () => page.removeEventListener("focusin", handleFocusIn);
  }, []);

  const getSlot = useCallback(
    (kind: SidePanelKind) => {
      if (kind === "docked") return dockedSlot;
      if (kind === "inspect") return inspectSlot;
      return taskSlot;
    },
    [dockedSlot, inspectSlot, taskSlot],
  );

  const isPanelActive = useCallback((panelId: string) => activeIds.has(panelId), [activeIds]);
  const isPanelStacked = useCallback((panelId: string) => stackedIds.has(panelId), [stackedIds]);

  const getFocusRegions = useCallback(() => {
    const regions: HTMLElement[] = [];
    if (pageRef.current && !isPageCovered) regions.push(pageRef.current);
    for (const slot of [
      isDockedCovered ? null : dockedSlot,
      isInspectCovered ? null : inspectSlot,
    ]) {
      const panel = slot?.querySelector<HTMLElement>(VISIBLE_COLUMN_PANEL_SELECTOR);
      if (panel) regions.push(panel);
    }
    return regions;
  }, [dockedSlot, inspectSlot, isDockedCovered, isInspectCovered, isPageCovered]);

  const focusRegion = useCallback((region: HTMLElement) => {
    if (region === pageRef.current) {
      const last = lastPageFocusRef.current;
      if (last?.isConnected && region.contains(last)) {
        last.focus();
        return;
      }
      // Nothing in the page had focus yet: its first control, else the page region itself.
      const first = Array.from(
        region.querySelectorAll<HTMLElement>(SIDE_PANEL_FOCUSABLE_SELECTOR),
      ).find((element) => !element.closest("[hidden], [inert]"));
      (first ?? region).focus();
      return;
    }
    const target =
      region.querySelector<HTMLElement>("[data-side-panel-title]") ??
      region.querySelector<HTMLElement>("button") ??
      region;
    target.focus();
  }, []);

  const handle = useMemo<SidePanelOutletHandle>(
    () => ({
      id,
      depth,
      ancestorIds,
      layout,
      registerPanel,
      getSlot,
      isPanelActive,
      isPanelStacked,
      getFocusRegions,
      focusRegion,
    }),
    [
      ancestorIds,
      depth,
      focusRegion,
      getFocusRegions,
      getSlot,
      id,
      isPanelActive,
      isPanelStacked,
      layout,
      registerPanel,
    ],
  );

  // Registered once for the outlet's life and updated in place, so outlets keep their order (the
  // first registered wins a tie in depth) and a panel opening re-renders only what reads it.
  const handleRef = useRef(handle);
  useLayoutEffect(() => {
    handleRef.current = handle;
    updateOutlet?.(handle);
  }, [handle, updateOutlet]);
  useLayoutEffect(() => registerOutlet?.(handleRef.current), [id, registerOutlet]);

  return (
    <SidePanelOutletContext.Provider value={handle}>
      <div
        ref={rootRef}
        data-side-panel-outlet=""
        data-layout={layout}
        data-open={hasColumnPanel || hasTask ? "" : undefined}
        className={cn(
          "flex min-w-0",
          // Overlay and task layers position against the outlet, under the navbar.
          (hasColumnPanel || hasTask) && "relative",
          className,
        )}
      >
        <div
          ref={pageRef}
          data-side-panel-page=""
          // Focusable only while F6 can return to it, so a click on a page with no panel open
          // leaves focus on the body as before.
          tabIndex={hasColumnPanel ? -1 : undefined}
          className={cn(
            "min-w-0 flex-1 outline-none",
            "focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-purple-100",
            "dark:focus-visible:ring-purple-40",
            pageClassName,
          )}
        >
          {children}
        </div>
        <div
          ref={columnRef}
          data-side-panel-column=""
          hidden={!hasColumnPanel || undefined}
          className={cn(
            // Never wider than the outlet: panels narrow together rather than run off screen.
            "flex max-w-full",
            !hasColumnPanel && "hidden",
            layout === "push" && "h-full flex-none",
            layout === "overlay" && "absolute inset-y-0 right-0 z-40",
            // Full screen draws over the app sidebar (z-50, earlier in the page); popovers and
            // dialogs portal to the end of <body> at z-50, so they still draw above the panel.
            layout === "full-screen" && "fixed inset-0 z-50",
          )}
        >
          {/* Docked first, so it sits nearest the page. */}
          <div
            ref={setDockedSlot}
            data-side-panel-slot="docked"
            hidden={isDockedCovered || undefined}
            inert={isDockedCovered || undefined}
            className={isDockedCovered ? undefined : "contents"}
          />
          <div
            ref={setInspectSlot}
            data-side-panel-slot="inspect"
            hidden={isInspectCovered || undefined}
            inert={isInspectCovered || undefined}
            className={isInspectCovered ? undefined : "contents"}
          />
        </div>
        <div ref={setTaskSlot} data-side-panel-slot="task" className="contents" />
      </div>
    </SidePanelOutletContext.Provider>
  );
}
